import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X } from 'lucide-react';
import Logo from './Logo';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, isAdmin, signOut } = useAuth();
  const location = useLocation();

  const closeMenu = () => setIsMenuOpen(false);
  const toggleMenu = () => setIsMenuOpen((open) => !open);

  const isActive = (path: string) => location.pathname === path;

  // Links shown to everyone vs. authenticated investors only
  const publicLinks = [{ to: '/about', label: 'About' }, { to: '/contact', label: 'Contact' }];
  const investorLinks = [
    { to: '/investments', label: 'Investments' },
    { to: '/dashboard', label: 'Dashboard' },
  ];
  const links = user
    ? [...publicLinks, ...investorLinks, ...(isAdmin ? [{ to: '/admin', label: 'Admin' }] : [])]
    : publicLinks;

  return (
    <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/80 backdrop-blur-md">
      <div className="container flex h-[72px] items-center justify-between">
        <Link to="/" onClick={closeMenu} className="flex items-center">
          <Logo className="h-8 w-auto" />
          <span className="ml-2.5 text-lg font-medium tracking-tight">UDCapital</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-8 md:flex">
          {links.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`text-sm font-medium transition-colors hover:text-white ${
                isActive(to) ? 'text-white' : 'text-zinc-400'
              }`}
            >
              {label}
            </Link>
          ))}

          {user ? (
            <button
              onClick={signOut}
              className="text-sm font-medium text-zinc-400 transition-colors hover:text-white"
            >
              Log Out
            </button>
          ) : (
            <Link to="/login" className="btn-primary">
              Log In
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden"
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="fade-in border-t border-zinc-900 bg-black md:hidden">
          <div className="container flex flex-col gap-1 py-4">
            {links.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                onClick={closeMenu}
                className={`py-2 text-base font-medium transition-colors ${
                  isActive(to) ? 'text-white' : 'text-zinc-400'
                }`}
              >
                {label}
              </Link>
            ))}

            {user ? (
              <button
                onClick={() => {
                  signOut();
                  closeMenu();
                }}
                className="py-2 text-left text-base font-medium text-zinc-400 transition-colors hover:text-white"
              >
                Log Out
              </button>
            ) : (
              <Link to="/login" onClick={closeMenu} className="py-2 text-base font-medium text-white">
                Log In
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
